import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { PaymentInteractionCard as SharedPaymentInteractionCard } from "@f2e/ui/components/Chat"
import type {
  PaymentInteractionRequestData,
  PaymentRequestSchema,
} from "@f2e/ui/chat/transport"

import {
  FetchStatus,
  useCancelPaymentRequestApi,
  useGetPaymentRequestStatusApi,
} from "@/hooks/api"
export interface InteractionPaymentCardProps {
  payment: PaymentRequestSchema
  onResolved?: () => void
}

export interface PendingInteractionPaymentCardProps {
  pendingPayment: PaymentInteractionRequestData
  onResolved?: () => void
}

const InteractionPaymentCard = ({
  payment,
  onResolved,
}: InteractionPaymentCardProps) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState(payment.status)
  const [statusUpdatedAt, setStatusUpdatedAt] = useState<string | undefined>()
  const checkoutWindowRef = useRef<Window | null>(null)

  const { fetchFn: fetchPaymentStatus, status: fetchStatus } =
    useGetPaymentRequestStatusApi(payment.paymentRequestId)
  const { fetchFn: cancelPaymentRequest, status: cancelStatus } =
    useCancelPaymentRequestApi(payment.paymentRequestId)

  const isActionable = status === "pending" || status === "processing"
  const isLoading = fetchStatus === FetchStatus.Loading
  const isCancelling = cancelStatus === FetchStatus.Loading

  useEffect(() => {
    setStatus(payment.status)
  }, [payment.status])

  const labels = useMemo(
    () => ({
      title: t("common.paymentInteraction.title"),
      defaultItemDesc: t("common.paymentInteraction.defaultItemDesc"),
      amount: t("common.paymentInteraction.amount"),
      orderNo: t("common.paymentInteraction.orderNo"),
      updatedAt: t("common.paymentInteraction.updatedAt"),
      openCheckout: t("common.paymentInteraction.openCheckout"),
      refreshStatus: t("common.paymentInteraction.refreshStatus"),
      cancelPayment: t("common.paymentInteraction.cancelPayment"),
      continueChat: t("common.paymentInteraction.continueChat"),
      getStatusLabel: (s: string) =>
        t(`common.paymentInteraction.status.${s}`, {
          defaultValue: s,
        }),
    }),
    [t]
  )

  const refreshStatus = useCallback(
    (preserveData = false) => {
      fetchPaymentStatus({
        preserveData,
        callbackFn: (resp) => {
          const next = resp?.data
          if (!next) return
          setStatus(next.status)
          setStatusUpdatedAt(next.updated_at)
        },
      })
    },
    [fetchPaymentStatus]
  )

  useEffect(() => {
    refreshStatus(true)
  }, [refreshStatus])

  useEffect(() => {
    if (!isActionable) return
    const timer = window.setInterval(() => {
      refreshStatus(true)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [isActionable, refreshStatus])

  useEffect(() => {
    const handlePaymentBridgeMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (
        checkoutWindowRef.current &&
        event.source !== checkoutWindowRef.current
      ) {
        return
      }
      const data = event.data
      if (!data || typeof data !== "object") return
      if ((data as { type?: string }).type !== "codeer.payment.return") return
      refreshStatus(true)
    }

    window.addEventListener("message", handlePaymentBridgeMessage)
    return () =>
      window.removeEventListener("message", handlePaymentBridgeMessage)
  }, [refreshStatus])

  useEffect(() => {
    const handlePaymentStorageEvent = (event: StorageEvent) => {
      if (event.key !== "codeer.payment.return") return
      if (!event.newValue) return
      refreshStatus(true)
    }

    window.addEventListener("storage", handlePaymentStorageEvent)
    return () =>
      window.removeEventListener("storage", handlePaymentStorageEvent)
  }, [refreshStatus])

  const handleOpenCheckout = useCallback(() => {
    if (!payment.checkoutUrl) return
    // Opening checkout is not enough to make the payment non-cancellable;
    // only a backend status refresh/resolver result can move it to processing.
    // Keep opener so /payments/bridge can postMessage back and close itself.
    checkoutWindowRef.current = window.open(payment.checkoutUrl, "_blank")
  }, [payment.checkoutUrl])

  const handleCancelPayment = useCallback(() => {
    const token = (() => {
      try {
        const url = new URL(payment.checkoutUrl)
        const parts = url.pathname.split("/").filter(Boolean)
        return parts[parts.length - 1] || ""
      } catch {
        return ""
      }
    })()

    const searchParams = token
      ? new URLSearchParams([["checkout_token", token]])
      : undefined

    cancelPaymentRequest({
      searchParams,
      callbackFn: (resp) => {
        const next = resp?.data
        if (!next) return
        setStatus(next.status)
        setStatusUpdatedAt(next.updated_at)
      },
    })
  }, [cancelPaymentRequest, payment.checkoutUrl])

  return (
    <SharedPaymentInteractionCard
      data={{
        itemDesc: payment.itemDesc,
        currency: payment.currency,
        amountTwd: payment.amountTwd,
        merchantOrderNo: payment.merchantOrderNo,
        status,
        updatedAt: statusUpdatedAt,
      }}
      labels={labels}
      onOpenCheckout={handleOpenCheckout}
      onRefreshStatus={() => refreshStatus()}
      onCancelPayment={handleCancelPayment}
      onContinueChat={!isActionable ? onResolved : undefined}
      refreshDisabled={isLoading || isCancelling}
      cancelDisabled={isLoading || isCancelling}
    />
  )
}

export const PendingInteractionPaymentCard = ({
  pendingPayment,
  onResolved,
}: PendingInteractionPaymentCardProps) => (
  <InteractionPaymentCard
    payment={pendingPayment.payment}
    onResolved={onResolved}
  />
)

export default InteractionPaymentCard
